import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, computed, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from '@env/environment';
import { firstValueFrom, Observable, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  isLoggedIn = signal(false);
  userId = computed(() => this.getUserId());
  userPhotoUrl = signal<string | null>(null);
  userInitials = computed(() => this.getUserInitials());
  userFirstName = computed(() => this.getUserFirstName());

  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly http = inject(HttpClient);
  private readonly onLoginSubject = new Subject<void>();

  private apiAuthToken: string | null = null;

  constructor() {
    this.init();
  }

  async init(): Promise<void> {
    this.msalBroadcastService.msalSubject$.subscribe(async (event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        this.isLoggedIn.set(true);
        await this.afterLoginInit();
      } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
        this.isLoggedIn.set(false);
        this.userPhotoUrl.set(null);
      }
    });

    this.isLoggedIn.set(!!this.getUser());

    if (this.isLoggedIn()) {
      await this.afterLoginInit();
    }
  }

  ngOnDestroy(): void {
    this.onLoginSubject.complete();
  }

  subscribOnLogin(): Observable<void> {
    return this.onLoginSubject.asObservable();
  }

  logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/uitgelogd';
  }

  getUser(): AccountInfo {
    return this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];
  }

  getUserEmail(): string {
    const user = this.getUser();
    return user?.username;
  }

  /** Returns true if the logged-in user has the given role (checks idToken and access token). */
  hasRole(role: string): boolean {
    const user = this.getUser();
    if (!user) return false;

    const idRoles = user.idTokenClaims?.['roles'];
    if (Array.isArray(idRoles) && idRoles.includes(role)) return true;

    if (this.apiAuthToken) {
      const accessRoles = this.decodeJwtRoles(this.apiAuthToken);
      if (accessRoles.includes(role)) return true;
    }
    return false;
  }

  private decodeJwtRoles(token: string): string[] {
    try {
      const parts = token.split('.');
      if (parts.length < 2) return [];
      const payload = JSON.parse(atob(parts[1].replaceAll('-', '+').replaceAll('_', '/')));
      const r = payload?.roles;
      return Array.isArray(r) ? (r as string[]) : [];
    } catch {
      return [];
    }
  }

  /** Returns true if the logged-in account is a Dura Vermeer employee (not a B2B guest). */
  isEmployee(): boolean {
    const user = this.getUser();
    if (!user) return false;
    // B2B guest accounts have '#EXT#' in their User Principal Name
    return !user.username?.includes('#EXT#');
  }

  getApiToken(): string | null {
    return this.apiAuthToken;
  }

  async fetchUserPhoto(): Promise<void> {
    const account = this.getUser();
    if (!account) {
      this.userPhotoUrl.set(null);
      return;
    }
    try {
      const accessToken = await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: ['user.read'],
      });
      const response = await firstValueFrom(
        this.http.get('https://graph.microsoft.com/v1.0/me/photos/48x48/$value', {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${accessToken.accessToken}`,
          },
        })
      );
      this.userPhotoUrl.set(URL.createObjectURL(response));
    } catch {
      this.userPhotoUrl.set(null);
    }
  }

  /**
   * Acquires an access token for the API scope. If the refresh token expired or there is no
   * server session (AADSTS160021 / interaction_required), starts an interactive redirect so the
   * user can sign in again — silent renewal cannot recover in that case.
   * @returns true if a redirect to sign-in was started (callers should skip follow-up work).
   */
  async fetchAuthToken(): Promise<boolean> {
    const account = this.getUser();
    if (!account) {
      this.apiAuthToken = null;
      return false;
    }

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: [environment.integrationPortalApi.scope],
      });
      this.apiAuthToken = result.accessToken;
      return false;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await this.msalService.instance.acquireTokenRedirect({
          account,
          scopes: [environment.integrationPortalApi.scope],
        });
        return true;
      }
      throw e;
    }
  }

  async fetchAuthTokenForScope(scope: string): Promise<string> {
    const account = this.getUser();
    if (!account) {
      throw new Error('No signed-in account');
    }

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: [scope],
      });
      return result.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await this.msalService.instance.acquireTokenRedirect({
          account,
          scopes: [scope],
        });
        return '';
      }
      throw e;
    }
  }

  private async afterLoginInit(): Promise<void> {
    await this.fetchUserPhoto();
    const redirected = await this.fetchAuthToken();
    if (redirected) {
      return;
    }
    this.onLoginSubject.next();
  }

  private getUserInitials(): string {
    const nameParts = this.getUserNameParts();
    if (!nameParts) return '';
    return (nameParts.first[0] + nameParts.last[0]).toUpperCase();
  }

  private getUserFirstName(): string {
    return this.getUserNameParts()?.first ?? '';
  }

  private getUserNameParts(): { first: string; last: string } | null {
    const name = this.getUser()?.name || '';
    if (!name.includes(',')) return null;

    const [last, first] = name.split(',').map((s) => s.trim());
    if (!first || !last) return null;

    return { first, last };
  }

  private getUserId(): string {
    const user = this.getUser();
    return user?.localAccountId || '';
  }
}

